import { mapActions, mapGetters } from 'vuex'
import { COOKIE_DURATION, cookieOpts } from '@/utils/cookieOptions'
import { CONSENT_COOKIE, hasMarketingConsent } from '@/utils/marketingConsent'
import {
  adoptIdentifiers,
  directTouch,
  hasIdentifiers,
  isDirectOnly,
  mergeTouch,
  stateForCookie,
  stateFromCookie,
  touchFromQuery,
  touchFromReferrer,
  touchesToSend,
  withoutIdentifiers,
} from '@/utils/attribution'
import {
  clearOverflowState,
  clearPendingAttribution,
  readOverflowState,
  readPendingAttribution,
  writeOverflowState,
  writePendingAttribution,
} from '@/utils/attributionStorage'
import {
  choosesSpareKit,
  orderTokenOf,
  sharedBulbConflictMessage,
  sharedBulbConflictOf,
  withSharedBulbSpare,
} from '@/utils/sharedBulbCart'
import { openingToSend, readStoriesOpening } from '@/utils/instagramStories'

let silentLoadInFlight = null
let consentListenerBound = false

const FB_COOKIE_MAX_AGE = 60 * 60 * 24 * 90
const ATTRIBUTION_COOKIE = 'analytics'
const CONSENT_EVENTS = ['CookiebotOnAccept', 'CookiebotOnConsentReady']

function readDomCookie(name) {
  if (typeof document === 'undefined') return null
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1')
  const match = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'))
  return match ? decodeURIComponent(match[1]) : null
}

function writeDomCookie(name, value, maxAgeSeconds) {
  if (typeof document === 'undefined') return
  const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax${secure}`
}

function ensureFacebookCookies(fbclid) {
  if (typeof document === 'undefined') return
  if (!hasMarketingConsent()) return
  const now = Date.now()

  if (fbclid && !readDomCookie('_fbc')) {
    writeDomCookie('_fbc', `fb.1.${now}.${fbclid}`, FB_COOKIE_MAX_AGE)
  }

  if (!readDomCookie('_fbp')) {
    const rand = Math.floor(Math.random() * 2147483647)
    writeDomCookie('_fbp', `fb.1.${now}.${rand}`, FB_COOKIE_MAX_AGE)
  }
}

export default {
  computed: {
    ...mapGetters({
      cart: 'cart',
    }),
    userCart: {
      get() {
        return this.cart ? this.cart : null
      },
      set(value) {
        this.setCart(value)
      },
    },
  },

  data() {
    return {
      addingProductToCart: false,
    }
  },

  created() {
    if (process.client && this.$cartChannel) {
      this.$_cartChannelUnsubscribe = this.$cartChannel.onChange(() => {
        this.loadCart({ silent: true })
      })
    }
  },

  beforeDestroy() {
    if (typeof this.$_cartChannelUnsubscribe === 'function') {
      this.$_cartChannelUnsubscribe()
      this.$_cartChannelUnsubscribe = null
    }
  },

  methods: {
    ...mapActions({
      setCart: 'setCart',
      setProductAddedToCart: 'setProductAddedToCart',
    }),

    async saveUserAgent(checkoutStep = null) {
      if (process.client && this.cart?.id) {
        await this.$orderService.userAgent(this.cart.id, {
          user_info: JSON.stringify(this.$ua._parsed),
          ...(checkoutStep ? { checkout_step: checkoutStep } : {}),
        })
      }
    },

    async addToCart(
      product,
      variant,
      quantity = 1,
      metadata = null,
      { showModal = true } = {}
    ) {
      this.addingProductToCart = true
      const cartId = this.$store.state.cart?.id
      let added = false

      try {
        const payload = {
          order_id: cartId,
          variant_id: variant.id,
          quantity,
        }
        if (metadata) payload.metadata = metadata
        const line = await this.createCartLine(payload)
        if (!line) return false
        added = true
        const token = orderTokenOf(line.data)
        if (token) this.setOrderToken(token)
        this.setProductAddedToCart(product)
        if (showModal) {
          this.$bvModal.show('modalProductAddedToCart')
        }

        let eventId = null
        try {
          eventId = this.$trackAddToCart(product, variant, quantity)
        } catch (error) {
          console.warn(
            `Erreur $trackAddToCart (product: ${product?.id}, variant: ${variant?.id})`,
            error
          )
        }

        if (cartId) {
          try {
            const params = { order_id: cartId }
            if (eventId) params.event_id = eventId
            await this.$metaConversionService.addToCart(params)
          } catch (error) {
            console.warn(`Erreur meta addToCart (cartId: ${cartId})`, error)
          }
        }
      } catch (error) {
        this.$swalErrorMessage(error)
      } finally {
        try {
          await Promise.all([this.loadCart(), this.saveUserAgent()])
        } catch (error) {
          console.warn('Erreur lors du refresh post-addToCart', error)
        } finally {
          this.addingProductToCart = false
        }
      }

      return added
    },

    async createCartLine(payload) {
      try {
        const response = await this.$orderProductService.create(payload)
        return { data: response?.data }
      } catch (error) {
        const conflict = sharedBulbConflictOf(error)
        if (!conflict) throw error

        this.addingProductToCart = false
        if (!(await this.confirmSharedBulbSpare(conflict))) return null

        this.addingProductToCart = true
        const response = await this.$orderProductService.create({
          ...payload,
          metadata: withSharedBulbSpare(payload.metadata),
        })
        return { data: response?.data }
      }
    },

    async confirmSharedBulbSpare(conflict) {
      const h = this.$createElement
      const message = h('div', { class: 'tw-flex tw-items-start tw-gap-3' }, [
        h(
          'span',
          {
            class:
              'tw-flex tw-items-center tw-justify-center tw-shrink-0 tw-size-9 tw-rounded-full tw-bg-amber-100 tw-text-amber-600',
          },
          [h('font-awesome-icon', { props: { icon: ['fas', 'lightbulb'] } })]
        ),
        h(
          'p',
          { class: 'tw-mb-0 tw-text-sm tw-leading-relaxed tw-text-gray-700' },
          sharedBulbConflictMessage(conflict, (key, params) =>
            this.$t(key, params)
          )
        ),
      ])

      const choice = await this.$bvModal.msgBoxConfirm([message], {
        title: this.$t('mixins.cartMixin.sharedBulbConflictTitle'),
        centered: true,
        okVariant: 'warning',
        okTitle: this.$t('mixins.cartMixin.sharedBulbConflictKeepOne'),
        cancelVariant: 'outline-secondary',
        cancelTitle: this.$t('mixins.cartMixin.sharedBulbConflictAddSpare'),
        contentClass: '!tw-rounded-2xl !tw-border-0 tw-overflow-hidden',
        headerClass: '!tw-border-b-0 !tw-pb-0',
        titleClass: 'tw-text-lg tw-font-bold tw-text-gray-900',
        bodyClass: '!tw-pt-3',
        footerClass: '!tw-border-t-0 !tw-pt-0',
      })

      return choosesSpareKit(choice)
    },

    setOrderToken(token) {
      this.$nuxtCookies.set('order_token', token, cookieOpts(COOKIE_DURATION.MONTH, { secure: true }))
    },

    storeAttributionState(state) {
      if (isDirectOnly(state)) return
      const compact = stateForCookie(state)
      if (compact) {
        this.$nuxtCookies.set(
          ATTRIBUTION_COOKIE,
          JSON.stringify(compact),
          cookieOpts(COOKIE_DURATION.MONTH, { secure: true })
        )
        clearOverflowState()
        return
      }
      this.removeAttributionCookie()
      writeOverflowState(state)
    },

    removeAttributionCookie() {
      if (this.$nuxtCookies.get(ATTRIBUTION_COOKIE)) {
        this.$nuxtCookies.remove(ATTRIBUTION_COOKIE, { path: '/' })
      }
    },

    forgetAttributionState() {
      this.removeAttributionCookie()
      clearOverflowState()
    },

    readAttributionState() {
      return (
        stateFromCookie(this.$nuxtCookies.get(ATTRIBUTION_COOKIE)) ||
        readOverflowState()
      )
    },

    async persistAttribution(state, cart) {
      const touches = cart?.id ? touchesToSend(state, cart) : []
      if (touches.length) {
        try {
          await this.$orderService.setAnalytics(cart.id, { touches })
        } catch (error) {
          console.error(error)
          this.storeAttributionState(state)
          return
        }
      }
      if (cart?.id) {
        this.forgetAttributionState()
      } else {
        this.storeAttributionState(state)
      }
    },

    async captureAttribution(cart) {
      const touch =
        touchFromQuery(this.$route.query) ||
        touchFromReferrer(document.referrer, window.location.hostname) ||
        directTouch()
      const marketingConsent = hasMarketingConsent(
        this.$nuxtCookies.get(CONSENT_COOKIE)
      )

      if (marketingConsent) {
        clearPendingAttribution()
        if (touch.fbclid) ensureFacebookCookies(touch.fbclid)
      } else if (hasIdentifiers(touch)) {
        writePendingAttribution(mergeTouch(readPendingAttribution(), touch))
        this.listenForMarketingConsent()
      }

      const captured = marketingConsent ? touch : withoutIdentifiers(touch)
      await this.persistAttribution(
        mergeTouch(this.readAttributionState(), captured),
        cart
      )
    },

    listenForMarketingConsent() {
      if (consentListenerBound) return
      consentListenerBound = true
      CONSENT_EVENTS.forEach(event =>
        window.addEventListener(event, () => this.flushPendingAttribution())
      )
    },

    async flushPendingAttribution() {
      if (!hasMarketingConsent()) return
      const pending = readPendingAttribution()
      if (!pending) return
      clearPendingAttribution()
      const fbclid = pending.last?.fbclid || pending.first?.fbclid
      if (fbclid) ensureFacebookCookies(fbclid)
      const cart = this.userCart
      const state = cart?.id
        ? pending
        : adoptIdentifiers(this.readAttributionState(), pending)
      await this.persistAttribution(state, cart)
    },

    async loadCart({ silent = false } = {}) {
      if (silent && silentLoadInFlight) {
        return silentLoadInFlight
      }

      const doLoad = this._doLoadCart(silent)
      if (silent) {
        silentLoadInFlight = doLoad.finally(() => {
          silentLoadInFlight = null
        })
        return silentLoadInFlight
      }
      return doLoad
    },

    cartLoadParams() {
      return this.$store.getters.cart_load_params || {}
    },

    shouldPushPlateVehicle(order, vehicle) {
      if (!order?.id || !vehicle?.id || !vehicle.search) return false

      return (
        !(order.vehicles || []).some(v => v.id === vehicle.id) &&
        !(order.dismissed_vehicle_ids || []).includes(vehicle.id)
      )
    },

    async _doLoadCart(silent) {
      if (
        this.$nuxtCookies.get('order_token') &&
        ['undefined', 'null'].includes(this.$nuxtCookies.get('order_token'))
      ) {
        this.$nuxtCookies.remove('order_token', { path: '/' })
      }

      const extraParams = { ...(this.cartLoadParams() || {}) }
      const extraIncludes = extraParams.include || []
      delete extraParams.include

      try {
        const { data } = await this.$orderService.get('current', {
          fastest_shipping_method_range: 1,
          ...extraParams,
          include: [
            'code.code_mode',
            'payment_method',
            'shipping_method_range.shipping_method.image',
            'shipping_method_range.shipping_method.image_eu',
            'shipping_method_range.shipping_method.image_int',
            'shipping_address.country',
            'billing_address.country',
            'order_payments.order_payment_status',
            'order_payments.payment_method.image',
            'order_global_status',
            'order_global_shipping_status',
            'order_global_payment_status',
            'order_variants.variant.variants_items',
            'order_variants.variant.variants_packs',
            'order_variants.variant.variant_type',
            'order_variants.variant.active_discount',
            'order_variants.variant.product.images',
            'order_variants.variant.filter_value_products.filter_value.filter',
            'order_variants.variant.images',
            'vehicles.brand',
            'user',
            ...extraIncludes,
          ].join(','),
        })

        this.setCart(data)

        if (!silent && process.client && this.$cartChannel) {
          this.$cartChannel.notify({ cartId: data?.id })
        }

        if (process.client) {
          await this.captureAttribution(data)
        }

        const selectedVehicle = this.$store.state.selected_vehicle
        if (data.id && selectedVehicle?.id && !data.source_vehicle_id) {
          try {
            await this.$orderService.setSourceVehicle(data.id, {
              source_vehicle_id: selectedVehicle.id,
            })
          } catch (e) {}
        }

        const storiesOpening = openingToSend(readStoriesOpening(), data)
        if (storiesOpening) {
          this.$orderService
            .setInstagramStoriesOpened(data.id, { opened_at: storiesOpening })
            .catch(() => {})
        }

        if (this.shouldPushPlateVehicle(data, selectedVehicle)) {
          try {
            await this.$orderService.addVehicle(data.id, {
              vehicle_id: selectedVehicle.id,
              origin: 'plate',
            })
            this.setCart({
              ...data,
              vehicles: [...(data.vehicles || []), selectedVehicle],
            })
          } catch (e) {}
        }
      } catch (error) {
        console.error(error)
      }
    },
  },
}
